import { relations, sql } from "drizzle-orm";
import {
  pgTable,
  text,
  timestamp,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

import { organization, user } from "./auth";

export const survey = pgTable(
  "survey",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    status: text("status").default("draft").notNull(),
    allowanceConsumedAt: timestamp("allowance_consumed_at"),
    libraryLastRefreshedAt: timestamp("library_last_refreshed_at"),
    snapshot: jsonb("snapshot"),
    surveyType: text("survey_type"),
    clientName: text("client_name"),
    vesselName: text("vessel_name"),
    make: text("make"),
    model: text("model"),
    loa: text("loa"),
    beam: text("beam"),
    draft: text("draft"),
    year: integer("year"),
    hullMaterial: text("hull_material"),
    fuel: text("fuel"),
    engine: text("engine"),
    vesselConfiguration: jsonb("vessel_configuration"),
    vesselConfigChips: text("vessel_config_chips")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    hin: text("hin"),
    mouldNumber: text("mould_number"),
    officialNumber: text("official_number"),
    registeredTonnage: text("registered_tonnage"),
    ssrNumber: text("ssr_number"),
    location: text("location"),
    surveyDate: timestamp("survey_date"),
    afloatAshore: text("afloat_ashore"),
    includeEquipment: boolean("include_equipment").notNull().default(false),
    insurancePurpose: boolean("insurance_purpose").notNull().default(false),
    surveyedAshoreLocation: text("surveyed_ashore_location"),
    surveyedAshoreDate: timestamp("surveyed_ashore_date"),
    surveyedAfloatLocation: text("surveyed_afloat_location"),
    surveyedAfloatDate: timestamp("surveyed_afloat_date"),
    weatherWindForce: integer("weather_wind_force"),
    weatherWindDirection: text("weather_wind_direction"),
    weatherSky: text("weather_sky"),
    weatherPrecipitation: text("weather_precipitation"),
    weatherTemperatureC: integer("weather_temperature_c"),
    createdBy: text("created_by").references(() => user.id),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("survey_organization_id_idx").on(table.organizationId)],
);

export const surveyArea = pgTable(
  "survey_area",
  {
    id: text("id").primaryKey(),
    surveyId: text("survey_id")
      .notNull()
      .references(() => survey.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("survey_area_survey_id_idx").on(table.surveyId)],
);

export const surveyItem = pgTable(
  "survey_item",
  {
    id: text("id").primaryKey(),
    surveyAreaId: text("survey_area_id")
      .notNull()
      .references(() => surveyArea.id, { onDelete: "cascade" }),
    atomicItemId: text("atomic_item_id").references(() => items.id, {
      onDelete: "restrict",
    }),
    title: text("title").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    relevant: boolean("relevant").default(true).notNull(),
    actionsOptions: text("actions_options"),
    findingsOptions: text("findings_options"),
    recommendationsOptions: text("recommendations_options"),
    snapshot: jsonb("snapshot"),
    generatedParagraph: text("generated_paragraph"),
    generationStatus: text("generation_status").default("draft").notNull(),
    generatedAt: timestamp("generated_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("survey_item_area_id_idx").on(table.surveyAreaId),
    index("survey_item_atomic_item_id_idx").on(table.atomicItemId),
  ],
);

export const surveyNote = pgTable(
  "survey_note",
  {
    id: text("id").primaryKey(),
    surveyItemId: text("survey_item_id")
      .notNull()
      .references(() => surveyItem.id, { onDelete: "cascade" }),
    actions: text("actions"),
    findings: text("findings"),
    notes: text("notes"),
    recommendations: text("recommendations"),
    recUrgency: text("rec_urgency"),
    aiParagraph: text("ai_paragraph"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("survey_note_item_id_idx").on(table.surveyItemId)],
);

export const template = pgTable(
  "template",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    fileKey: text("file_key").notNull(),
    version: integer("version").default(1).notNull(),
    requiredHeadings: text("required_headings"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("template_organization_id_idx").on(table.organizationId)],
);

export const surveyItemLibrary = pgTable(
  "survey_item_library",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id").references(() => organization.id, {
      onDelete: "cascade",
    }),
    itemTitle: text("item_title").notNull(),
    actionsOptions: text("actions_options"),
    findingsOptions: text("findings_options"),
    recommendationsOptions: text("recommendations_options"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("survey_item_library_org_id_idx").on(table.organizationId),
    index("survey_item_library_item_title_idx").on(table.itemTitle),
    uniqueIndex("survey_item_library_org_item_unique").on(
      table.organizationId,
      table.itemTitle,
    ),
  ],
);

export const vesselConfigurationOption = pgTable(
  "vessel_configuration_option",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    category: text("category").notNull(),
    label: text("label").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("vessel_config_option_org_cat_idx").on(
      table.organizationId,
      table.category,
    ),
    uniqueIndex("vessel_config_option_org_cat_label_unique").on(
      table.organizationId,
      table.category,
      table.label,
    ),
  ],
);

export const items = pgTable(
  "items",
  {
    id: text("id").primaryKey(),
    itemTitle: text("item_title").notNull().unique(),
    mainItem: text("main_item"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("items_item_title_idx").on(table.itemTitle)],
);

export const phrases = pgTable(
  "phrases",
  {
    id: text("id").primaryKey(),
    itemId: text("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    appliesTo: text("applies_to").default("action").notNull(),
    actionPhraseId: text("action_phrase_id"),
    subItemIds: text("sub_item_ids")
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    bucketType: text("bucket_type").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    phraseText: text("phrase_text").notNull(),
    defaultSelected: boolean("default_selected").default(true).notNull(),
    supportsNegation: boolean("supports_negation").default(false).notNull(),
    supportsQualifier: boolean("supports_qualifier").default(false).notNull(),
    qualifierPosition: text("qualifier_position"),
    defaultNegation: text("default_negation").default("").notNull(),
    scope: text("scope").default("org").notNull(),
    organizationId: text("organization_id").references(() => organization.id, {
      onDelete: "cascade",
    }),
    surveyId: text("survey_id").references(() => survey.id, {
      onDelete: "cascade",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("phrases_item_bucket_idx").on(table.itemId, table.bucketType),
    index("idx_phrases_action_phrase_id").on(table.actionPhraseId),
    index("phrases_bucket_text_idx").on(table.bucketType, table.phraseText),
    uniqueIndex("phrases_unique_org")
      .on(
        table.itemId,
        table.bucketType,
        table.phraseText,
        table.organizationId,
      )
      .where(sql`${table.scope} = 'org'`),
    uniqueIndex("phrases_unique_survey")
      .on(table.itemId, table.bucketType, table.phraseText, table.surveyId)
      .where(sql`${table.scope} = 'survey'`),
  ],
);

export const atomicInspectionTargets = pgTable(
  "atomic_inspection_targets",
  {
    id: text("id").primaryKey(),
    itemId: text("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    organizationId: text("organization_id").references(() => organization.id, {
      onDelete: "cascade",
    }),
    name: text("name").notNull(),
    quantityMode: text("quantity_mode").default("default").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("atomic_inspection_targets_item_sort_idx").on(
      table.itemId,
      table.sortOrder,
    ),
    index("atomic_inspection_targets_org_item_sort_idx").on(
      table.organizationId,
      table.itemId,
      table.sortOrder,
    ),
  ],
);

export const atomicInspectionTemplate = pgTable(
  "atomic_inspection_template",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    itemId: text("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    targetId: text("target_id")
      .notNull()
      .references(() => atomicInspectionTargets.id, { onDelete: "cascade" }),
    data: jsonb("data").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("atomic_inspection_template_org_item_target_uidx").on(
      table.organizationId,
      table.itemId,
      table.targetId,
    ),
    index("atomic_inspection_template_org_item_idx").on(
      table.organizationId,
      table.itemId,
    ),
    index("atomic_inspection_template_target_id_idx").on(table.targetId),
  ],
);

export const qualifiers = pgTable(
  "qualifiers",
  {
    id: text("id").primaryKey(),
    itemId: text("item_id").references(() => items.id, { onDelete: "cascade" }),
    text: text("text").notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("qualifiers_sort_order_idx").on(table.sortOrder),
    index("qualifiers_item_sort_order_idx").on(table.itemId, table.sortOrder),
    index("qualifiers_item_text_idx").on(table.itemId, table.text),
  ],
);

export const exportJob = pgTable(
  "export_job",
  {
    id: text("id").primaryKey(),
    surveyId: text("survey_id")
      .notNull()
      .references(() => survey.id, { onDelete: "cascade" }),
    status: text("status").default("queued").notNull(),
    outputFileKey: text("output_file_key"),
    generatorVersion: text("generator_version"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("export_job_survey_id_idx").on(table.surveyId)],
);

export const attachment = pgTable(
  "attachment",
  {
    id: text("id").primaryKey(),
    surveyItemId: text("survey_item_id")
      .notNull()
      .references(() => surveyItem.id, { onDelete: "cascade" }),
    fileKey: text("file_key").notNull(),
    filename: text("filename").notNull(),
    contentType: text("content_type"),
    size: integer("size"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [index("attachment_item_id_idx").on(table.surveyItemId)],
);

export const surveyRelations = relations(survey, ({ one, many }) => ({
  organization: one(organization, {
    fields: [survey.organizationId],
    references: [organization.id],
  }),
  areas: many(surveyArea),
  exports: many(exportJob),
  creator: one(user, {
    fields: [survey.createdBy],
    references: [user.id],
  }),
}));

export const surveyAreaRelations = relations(surveyArea, ({ one, many }) => ({
  survey: one(survey, {
    fields: [surveyArea.surveyId],
    references: [survey.id],
  }),
  items: many(surveyItem),
}));

export const surveyItemRelations = relations(surveyItem, ({ one, many }) => ({
  area: one(surveyArea, {
    fields: [surveyItem.surveyAreaId],
    references: [surveyArea.id],
  }),
  notes: many(surveyNote),
  attachments: many(attachment),
}));

export const surveyNoteRelations = relations(surveyNote, ({ one }) => ({
  item: one(surveyItem, {
    fields: [surveyNote.surveyItemId],
    references: [surveyItem.id],
  }),
}));

export const templateRelations = relations(template, ({ one }) => ({
  organization: one(organization, {
    fields: [template.organizationId],
    references: [organization.id],
  }),
}));

export const exportJobRelations = relations(exportJob, ({ one }) => ({
  survey: one(survey, {
    fields: [exportJob.surveyId],
    references: [survey.id],
  }),
}));

export const attachmentRelations = relations(attachment, ({ one }) => ({
  item: one(surveyItem, {
    fields: [attachment.surveyItemId],
    references: [surveyItem.id],
  }),
}));
